import type { Feature, GeoJSON as GeoJSONType } from 'geojson'
import type L from 'leaflet'
import { useCallback } from 'react'
import {
  FeatureGroup,
  GeoJSON,
  type GeoJSONProps,
  LayersControl as LeafletLayersControl,
  LayerGroup,
} from 'react-leaflet'
import { getRandomColor } from '../../utils/mapDrawHandle'
import { propertiesPopupContent } from '../../utils/popupContent'

const getColor = (feature: Feature) => {
  if (feature?.properties?.type === 'perimeter') {
    return 'black'
  }
  return feature?.properties?.color ?? getRandomColor()
}

interface LayerPaneProps {
  name: string
  children: React.ReactNode
  checked?: boolean
}

// The <Pane> wrapper must NOT be used here: when a layer is rendered inside a
// named Pane, its DOM elements are placed directly in the pane's container div.
// LayersControl.Overlay toggling works by calling map.removeLayer(), which
// removes the Leaflet layer object from the layer tree but does NOT empty the
// pane's DOM container — so the layer remains visible even after being "hidden".
// Using Overlay without a Pane wrapper makes the FeatureGroup own its DOM
// lifecycle and respond correctly to add/remove.
const LayerPane: React.FC<LayerPaneProps> = ({ name, children, checked = false }) => {
  return (
    <LeafletLayersControl.Overlay checked={checked} name={name}>
      <FeatureGroup>
        <LayerGroup>{children}</LayerGroup>
      </FeatureGroup>
    </LeafletLayersControl.Overlay>
  )
}

interface GeoJSONLayerProps {
  data: GeoJSONType
  layerStyles?: L.CircleMarkerOptions
  onEachFeature?: (feature: Feature, layer: L.Layer) => void
  pointToLayer?: GeoJSONProps['pointToLayer']
}

const EMPTY_OBJ = {}

const GeoJSONLayer: React.FC<GeoJSONLayerProps> = ({
  data,
  layerStyles = EMPTY_OBJ,
  onEachFeature,
  pointToLayer,
}) => {
  const defaultOnEachFeature = (feature: Feature, geoLayer: L.Layer) => {
    geoLayer.bindPopup(propertiesPopupContent(feature.properties))
  }

  // react-leaflet re-runs setStyle whenever the style prop changes identity, and getColor
  // falls back to a random colour, so an inline callback would repaint every feature a new
  // colour on each render of the host map.
  const style = useCallback(
    (feature: Feature) => ({
      color: getColor(feature),
      weight: 1,
      opacity: 1,
      fillOpacity: 0.2,
      ...layerStyles,
    }),
    [layerStyles],
  )

  return (
    <GeoJSON
      data={data}
      pointToLayer={pointToLayer}
      style={style}
      onEachFeature={onEachFeature || defaultOnEachFeature}
    />
  )
}

interface LayersControlProps {
  children: React.ReactNode
  collapsed?: boolean
}

export const LayersControl: React.FC<LayersControlProps> & {
  Pane: React.FC<LayerPaneProps>
  GeoJSONLayer: React.FC<GeoJSONLayerProps>
} = ({ children, collapsed = false }) => {
  return <LeafletLayersControl collapsed={collapsed}>{children}</LeafletLayersControl>
}

LayersControl.Pane = LayerPane
LayersControl.GeoJSONLayer = GeoJSONLayer
